import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, RefreshCw, Plus, ChevronDown, ChevronUp,
  Search, X, Download, Flame, MousePointerClick, Calendar,
} from 'lucide-react';

import useAutoLogout from '../hooks/useAutoLogout';
import Toast from '../components/Toast';
import { useTheme } from '../components/Navbar';
import API_BASE from '../config';

/* ── Theme tokens ────────────────────────────────────────────────────── */
const TKL = {
  light: {
    bg: '#ffffff', canvas: '#F5F6FA', border: '#ECEEF2',
    text: '#0F1623', sub: '#6B7280', muted: '#9CA3AF',
    accent: '#5B4EE8', accentBg: '#EEF0FD', accentTxt: '#4338CA',
    hover: '#F7F8FA', track: '#EEF0F4',
    shadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
    grid: 'rgba(15,22,35,0.06)', plot: '#FAFBFE',
  },
  dark: {
    bg: '#131320', canvas: '#0D0D14', border: '#1E1E2E',
    text: '#F1F2F6', sub: '#8B8FA8', muted: '#4B5263',
    accent: '#7C72FF', accentBg: 'rgba(124,114,255,0.12)', accentTxt: '#A5A0FF',
    hover: '#1A1A2A', track: '#1A1A2A',
    shadow: '0 1px 3px rgba(0,0,0,0.4)',
    grid: 'rgba(255,255,255,0.05)', plot: '#0F0F1A',
  },
};

const SVG_W = 960;
const SVG_H = 540;

/* ── Field accessors ─────────────────────────────────────────────────── */
const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
const evX       = (e) => num(e.x_coord ?? e.click_x);
const evY       = (e) => num(e.y_coord ?? e.click_y);
const evVW      = (e) => num(e.viewport_width ?? e.vw) || 1920;
const evVH      = (e) => num(e.viewport_height ?? e.vh) || 1080;
const evType    = (e) => (e.event_type ?? e.type ?? 'click');
const evId      = (e) => (e.event_id ?? e.id ?? null);
const evTenant  = (e) => (e.tenant_id ?? e.tenant ?? '');
const evProfile = (e) => (e.profile_id ?? '');
const evTime    = (e) => (e.recorded_at ?? e.created_at ?? null);
const pgUrlOf   = (p) => (p.page_url ?? p.url ?? '');
const pgClicksOf = (p) => {
  const n = parseInt(p.clicks ?? p.count ?? p.click_count ?? 0, 10);
  return Number.isFinite(n) ? n : 0;
};
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

/* ── IST timestamp ───────────────────────────────────────────────────── */
function fmtTime(raw) {
  if (!raw) return '—';
  let s = String(raw);
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s.replace(' ', 'T') + 'Z';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

function typePalette(type, isDark) {
  const t = String(type || '').toLowerCase();
  if (t === 'click')  return { solid: isDark ? '#FBBF24' : '#D97706', bg: isDark ? 'rgba(245,158,11,0.18)' : 'rgba(217,119,6,0.10)',   label: 'Click'  };
  if (t === 'scroll') return { solid: isDark ? '#60A5FA' : '#2563EB', bg: isDark ? 'rgba(59,130,246,0.18)'  : 'rgba(37,99,235,0.10)',   label: 'Scroll' };
  if (t === 'hover')  return { solid: isDark ? '#34D399' : '#059669', bg: isDark ? 'rgba(16,185,129,0.18)'  : 'rgba(5,150,105,0.10)',   label: 'Hover'  };
  return              { solid: isDark ? '#A5A0FF' : '#5B4EE8',        bg: isDark ? 'rgba(124,114,255,0.18)' : 'rgba(91,78,232,0.10)',   label: type || '—' };
}

/* ── Keyframes (once) ────────────────────────────────────────────────── */
if (typeof document !== 'undefined' && !document.getElementById('mt-heatmap-anim')) {
  const s = document.createElement('style');
  s.id = 'mt-heatmap-anim';
  s.textContent =
    '@keyframes mtShimmer{0%{background-position:100% 0}100%{background-position:-100% 0}}' +
    '@keyframes mtFade{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}' +
    '.mt-fade{animation:mtFade .25s ease}';
  document.head.appendChild(s);
}

/* ── Inline helpers ──────────────────────────────────────────────────── */
function useCountUp(target, duration = 850) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const to = Number(target) || 0;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function AnimatedNumber({ value, style }) {
  const v = useCountUp(value);
  return <span style={style}>{v.toLocaleString('en-IN')}</span>;
}

function Skel({ w = '100%', h = 16, r = 8, style }) {
  const { theme } = useTheme();
  const dark = theme === 'dark';
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: `linear-gradient(90deg,${dark?'#1A1A2A':'#ECEEF4'} 25%,${dark?'#23233A':'#F6F7FA'} 37%,${dark?'#1A1A2A':'#ECEEF4'} 63%)`,
      backgroundSize: '400% 100%', animation: 'mtShimmer 1.4s ease infinite',
      ...style,
    }} />
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
export default function Heatmap() {
  const navigate = useNavigate();
  useAutoLogout();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const tk = TKL[theme] || TKL.light;
  const drillRef = useRef(null);

  /* ── data ── */
  const [summary,       setSummary]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [pageUrl,       setPageUrl]       = useState('');
  const [events,        setEvents]        = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [hovered,       setHovered]       = useState(null);
  const [vizMode,       setVizMode]       = useState('points');

  /* ── ui ── */
  const [pageSearch,  setPageSearch]  = useState('');
  const [recordOpen,  setRecordOpen]  = useState(false);
  const [recording,   setRecording]   = useState(false);
  const [toast,       setToast]       = useState(null);
  const [form, setForm] = useState({ page_url: '', x_coord: '', y_coord: '', event_type: 'click', tenant_id: '' });

  /* ── GLOBAL FILTERS — always visible ── */
  const [gFrom,   setGFrom]   = useState('');
  const [gTo,     setGTo]     = useState('');
  const [gType,   setGType]   = useState('all');
  const [gTenant, setGTenant] = useState('all');

  const token =
    localStorage.getItem('martech_token') || sessionStorage.getItem('martech_token') ||
    localStorage.getItem('token')         || sessionStorage.getItem('token');

  const showToast = (msg, variant = 'green') => {
    setToast({ msg, variant });
    setTimeout(() => setToast(null), 3100);
  };

  const fetchSummary = () => {
    setLoading(true);
    fetch(`${API_BASE}/api/v1/heatmap/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.success) setSummary(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEvents = async (overrideUrl) => {
    const url = (overrideUrl ?? pageUrl).trim();
    if (!url) return;
    setPageUrl(url);
    setEventsLoading(true);
    setEvents(null);
    setHovered(null);
    try {
      const r = await fetch(
        `${API_BASE}/api/v1/heatmap/data?page_url=${encodeURIComponent(url)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const d = await r.json();
      setEvents(d.success ? (d.events || []) : []);
    } catch {
      setEvents([]);
    }
    setEventsLoading(false);
  };

  const recordEvent = async () => {
    if (!form.page_url.trim()) return;
    setRecording(true);
    try {
      const r = await fetch(`${API_BASE}/api/v1/heatmap/record`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_url:   form.page_url,
          x_coord:    parseFloat(form.x_coord) || 0,
          y_coord:    parseFloat(form.y_coord) || 0,
          event_type: form.event_type,
          tenant_id:  form.tenant_id,
          profile_id: '',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (d.success || r.ok) {
        showToast('Event recorded', 'green');
        setForm({ page_url: '', x_coord: '', y_coord: '', event_type: 'click', tenant_id: '' });
        if (pageUrl && pageUrl.trim() === form.page_url.trim()) fetchEvents(pageUrl);
      } else {
        showToast('Could not record event', 'red');
      }
    } catch {
      showToast('Could not record event', 'red');
    }
    setRecording(false);
  };

  /* ── derived ─────────────────────────────────────────────────────── */
  const pages = useMemo(() => {
    const list = (summary?.pages || []).slice();
    list.sort((a, b) => pgClicksOf(b) - pgClicksOf(a));
    return list;
  }, [summary]);

  const filteredPages = useMemo(() => {
    const q = pageSearch.trim().toLowerCase();
    return pages.filter(p => !q || pgUrlOf(p).toLowerCase().includes(q));
  }, [pages, pageSearch]);

  const totalEvents = summary?.total ?? summary?.total_events ?? pages.reduce((s, p) => s + pgClicksOf(p), 0);
  const pagesCount  = pages.length;
  const mostActive  = pages[0] || null;
  const peakClicks  = mostActive ? pgClicksOf(mostActive) : 0;
  const maxClicks   = peakClicks || 1;

  /* tenants from current event set */
  const tenants = useMemo(() => {
    if (!events) return [];
    return Array.from(new Set(events.map(evTenant).filter(Boolean)));
  }, [events]);

  /* apply ALL global filters to events */
  const filteredEvents = useMemo(() => {
    if (!events) return [];
    return events.filter(e => {
      if (gType !== 'all' && String(evType(e)).toLowerCase() !== gType) return false;
      if (gTenant !== 'all' && evTenant(e) !== gTenant) return false;
      const t = evTime(e);
      if (gFrom || gTo) {
        if (!t) return false;
        let s = String(t);
        if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s.replace(' ', 'T') + 'Z';
        const ms = new Date(s).getTime();
        if (gFrom && ms < new Date(gFrom + 'T00:00:00').getTime()) return false;
        if (gTo   && ms > new Date(gTo   + 'T23:59:59').getTime()) return false;
      }
      return true;
    });
  }, [events, gType, gTenant, gFrom, gTo]);

  const activeFilterCount =
    (gType !== 'all' ? 1 : 0) + (gTenant !== 'all' ? 1 : 0) +
    (gFrom ? 1 : 0) + (gTo ? 1 : 0);

  const clearFilters = () => { setGType('all'); setGTenant('all'); setGFrom(''); setGTo(''); };

  const plotted = filteredEvents.filter(e => evX(e) != null && evY(e) != null);

  const exportCSV = () => {
    if (!filteredEvents.length) return;
    const rows = [['event_id','page_url','x','y','event_type','profile_id','tenant_id','recorded_at']];
    filteredEvents.forEach(e => rows.push([
      evId(e) ?? '', e.page_url ?? pageUrl, evX(e) ?? '', evY(e) ?? '',
      evType(e), evProfile(e), evTenant(e), evTime(e) ?? '',
    ]));
    const csv  = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = `heatmap_${(pageUrl||'events').replace(/[^a-z0-9]/gi,'_').slice(0,40)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  /* ── shared styles ───────────────────────────────────────────────── */
  const card = { background: tk.bg, border: `1px solid ${tk.border}`, borderRadius: 16, boxShadow: tk.shadow };
  const inp  = {
    background: isDark ? tk.canvas : tk.bg, border: `1px solid ${tk.border}`,
    borderRadius: 10, color: tk.text, fontSize: 13, padding: '9px 12px', outline: 'none',
  };
  const focusOn  = e => { e.target.style.borderColor = tk.accent; };
  const focusOff = e => { e.target.style.borderColor = tk.border; };

  const stats = [
    { label: 'Total events',     value: totalEvents,                            numeric: true,  sub: 'All recorded interactions', pal: typePalette('hover',  isDark) },
    { label: 'Pages tracked',    value: pagesCount,                             numeric: true,  sub: 'Unique URLs monitored',     pal: typePalette('other',  isDark) },
    { label: 'Most active page', value: mostActive ? pgUrlOf(mostActive) : '—', numeric: false, sub: 'Highest interaction URL',   pal: typePalette('scroll', isDark) },
    { label: 'Peak clicks',      value: peakClicks,                             numeric: true,  sub: 'On the busiest page',       pal: typePalette('click',  isDark) },
  ];

  /* ═══════════════════════════ RENDER ════════════════════════════════ */
  return (
    <div style={{ minHeight: '100vh', background: tk.canvas, color: tk.text }}>
      <div style={{ height: 56 }} className="md:hidden" />
      {toast && <Toast message={toast.msg} variant={toast.variant} onDismiss={() => setToast(null)} />}

      <main className="px-4 md:px-7 py-6 pt-5 md:pt-8" style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* ── Header ── */}
        <div className="flex items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div style={{
              width: 42, height: 42, borderRadius: 12, flexShrink: 0,
              background: typePalette('click', isDark).bg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Activity size={20} color={typePalette('click', isDark).solid} />
            </div>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px', margin: 0, color: tk.text }}>Heatmap</h1>
              <p style={{ fontSize: 13, color: tk.sub, margin: '2px 0 0' }}>Interaction tracking across your landing pages</p>
            </div>
          </div>
          <button
            type="button" onClick={fetchSummary}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
              background: tk.bg, border: `1px solid ${tk.border}`, color: tk.sub,
              borderRadius: 10, padding: '9px 13px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            <span className="hidden md:inline">Refresh</span>
          </button>
        </div>

        {/* ══════════════════════════════════════════════════════════
            GLOBAL FILTER BAR — always visible, above stat cards
        ══════════════════════════════════════════════════════════ */}
        <div style={{ ...card, marginBottom: 20, padding: '16px 20px' }}>

          {/* Row 1 — title + clear */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 8, background: tk.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Calendar size={14} color={tk.accent} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: tk.text }}>Filters</span>
              {activeFilterCount > 0 && (
                <span style={{ background: tk.accent, color: '#fff', borderRadius: 99, fontSize: 10, fontWeight: 700, padding: '2px 8px' }}>
                  {activeFilterCount} active
                </span>
              )}
            </div>
            {activeFilterCount > 0 && (
              <button
                type="button" onClick={clearFilters}
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'transparent', border: `1px solid ${tk.border}`, borderRadius: 8, padding: '5px 11px', color: tk.sub, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
              >
                <X size={12} /> Clear all
              </button>
            )}
          </div>

          {/* Row 2 — filter controls, responsive grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>

            {/* From date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: tk.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                From date
              </label>
              <input
                type="date" value={gFrom}
                onChange={e => setGFrom(e.target.value)}
                style={{ ...inp, width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' }}
                onFocus={focusOn} onBlur={focusOff}
              />
            </div>

            {/* To date */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: tk.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                To date
              </label>
              <input
                type="date" value={gTo}
                onChange={e => setGTo(e.target.value)}
                style={{ ...inp, width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' }}
                onFocus={focusOn} onBlur={focusOff}
              />
            </div>

            {/* Event type */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 10, fontWeight: 700, color: tk.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Event type
              </label>
              <select
                value={gType} onChange={e => setGType(e.target.value)}
                style={{ ...inp, width: '100%', padding: '8px 10px', fontSize: 13, appearance: 'none', cursor: 'pointer', boxSizing: 'border-box' }}
              >
                <option value="all">All types</option>
                <option value="click">Click</option>
                <option value="scroll">Scroll</option>
                <option value="hover">Hover</option>
              </select>
            </div>

            {/* Tenant — appears once events are loaded */}
            {tenants.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: 10, fontWeight: 700, color: tk.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Tenant
                </label>
                <select
                  value={gTenant} onChange={e => setGTenant(e.target.value)}
                  style={{ ...inp, width: '100%', padding: '8px 10px', fontSize: 13, appearance: 'none', cursor: 'pointer', boxSizing: 'border-box' }}
                >
                  <option value="all">All tenants</option>
                  {tenants.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* Row 3 — active filter pills */}
          {activeFilterCount > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              {gFrom && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: tk.accentBg, color: tk.accentTxt, borderRadius: 99, fontSize: 11.5, fontWeight: 600, padding: '3px 10px' }}>
                  From: {gFrom}
                  <button type="button" onClick={() => setGFrom('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tk.accentTxt, padding: 0, display: 'flex' }}><X size={11} /></button>
                </span>
              )}
              {gTo && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: tk.accentBg, color: tk.accentTxt, borderRadius: 99, fontSize: 11.5, fontWeight: 600, padding: '3px 10px' }}>
                  To: {gTo}
                  <button type="button" onClick={() => setGTo('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tk.accentTxt, padding: 0, display: 'flex' }}><X size={11} /></button>
                </span>
              )}
              {gType !== 'all' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: typePalette(gType,isDark).bg, color: typePalette(gType,isDark).solid, borderRadius: 99, fontSize: 11.5, fontWeight: 600, padding: '3px 10px' }}>
                  Type: {gType}
                  <button type="button" onClick={() => setGType('all')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: typePalette(gType,isDark).solid, padding: 0, display: 'flex' }}><X size={11} /></button>
                </span>
              )}
              {gTenant !== 'all' && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, background: tk.accentBg, color: tk.accentTxt, borderRadius: 99, fontSize: 11.5, fontWeight: 600, padding: '3px 10px' }}>
                  Tenant: {gTenant}
                  <button type="button" onClick={() => setGTenant('all')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: tk.accentTxt, padding: 0, display: 'flex' }}><X size={11} /></button>
                </span>
              )}
            </div>
          )}
        </div>

        {/* ── Stat cards ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
          {stats.map(s => (
            <div key={s.label} style={{ ...card, padding: 18 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: s.pal.bg, marginBottom: 14 }} />
              {loading ? (
                <Skel w="60%" h={26} style={{ marginBottom: 6 }} />
              ) : s.numeric ? (
                <AnimatedNumber value={s.value} style={{ fontSize: 28, fontWeight: 800, color: s.pal.solid, letterSpacing: '-0.5px' }} />
              ) : (
                <p title={s.value} style={{ fontSize: 14, fontWeight: 700, color: s.pal.solid, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.value}
                </p>
              )}
              <p style={{ fontSize: 12.5, color: tk.text, fontWeight: 600, margin: '8px 0 1px' }}>{s.label}</p>
              <p style={{ fontSize: 11, color: tk.muted, margin: 0 }}>{s.sub}</p>
            </div>
          ))}
        </div>

        {/* ── Pages by volume ── */}
        <div style={{ ...card, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${tk.border}` }} className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: tk.text, margin: 0 }}>Pages by interaction volume</h2>
              <p style={{ fontSize: 12, color: tk.sub, margin: '2px 0 0' }}>
                {loading ? '—' : `${pagesCount} pages tracked`} · tap a row to inspect
              </p>
            </div>
            <div style={{ position: 'relative', minWidth: 200 }} className="w-full sm:w-auto">
              <Search size={14} color={tk.muted} style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }} />
              <input
                value={pageSearch} onChange={e => setPageSearch(e.target.value)}
                placeholder="Filter pages…"
                style={{ ...inp, paddingLeft: 32, width: '100%' }}
                onFocus={focusOn} onBlur={focusOff}
              />
            </div>
          </div>

          {loading ? (
            <div style={{ padding: 20, display: 'grid', gap: 10 }}>
              {Array(5).fill(null).map((_, i) => <Skel key={i} h={40} r={10} />)}
            </div>
          ) : filteredPages.length === 0 ? (
            <div style={{ padding: '56px 20px', textAlign: 'center' }}>
              <Activity size={36} color={tk.muted} style={{ marginBottom: 12 }} />
              <p style={{ fontSize: 14, fontWeight: 600, color: tk.sub, margin: 0 }}>
                {pageSearch ? 'No pages match that filter.' : 'No heatmap data yet.'}
              </p>
              <p style={{ fontSize: 12, color: tk.muted, margin: '4px 0 0' }}>
                {pageSearch ? 'Clear the search to see all tracked pages.' : 'Add tracker.js to your site to start collecting events.'}
              </p>
            </div>
          ) : (
            <>
              {/* desktop */}
              <div className="hidden md:block" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: tk.hover }}>
                      {['#','Page URL','Clicks','Volume','Action'].map(c => (
                        <th key={c} style={{ padding: '11px 18px', textAlign: 'left', color: tk.muted, fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: `1px solid ${tk.border}` }}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPages.map((p, i) => {
                      const clicks = pgClicksOf(p);
                      const pct = clamp((clicks / maxClicks) * 100, 2, 100);
                      const url = pgUrlOf(p);
                      return (
                        <tr
                          key={url || i}
                          onClick={() => { fetchEvents(url); drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                          style={{ borderBottom: `1px solid ${tk.border}`, cursor: 'pointer' }}
                          onMouseEnter={e => { e.currentTarget.style.background = tk.hover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <td style={{ padding: '13px 18px', color: tk.muted, fontSize: 12, fontWeight: 700 }}>{i + 1}</td>
                          <td style={{ padding: '13px 18px', color: tk.text, fontSize: 12.5, fontFamily: 'ui-monospace,monospace', maxWidth: 360 }}>
                            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url || '—'}</span>
                          </td>
                          <td style={{ padding: '13px 18px', color: tk.accent, fontWeight: 800, fontSize: 13 }}>{clicks.toLocaleString('en-IN')}</td>
                          <td style={{ padding: '13px 18px', minWidth: 200 }}>
                            <div style={{ height: 7, borderRadius: 99, background: tk.track, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: `linear-gradient(90deg,${tk.accent},${isDark?'#A5A0FF':'#8B5CF6'})`, transition: 'width .6s ease' }} />
                            </div>
                          </td>
                          <td style={{ padding: '13px 18px' }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: tk.accent }}>Inspect →</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* mobile */}
              <div className="md:hidden" style={{ padding: 14, display: 'grid', gap: 10 }}>
                {filteredPages.map((p, i) => {
                  const clicks = pgClicksOf(p);
                  const pct = clamp((clicks / maxClicks) * 100, 2, 100);
                  const url = pgUrlOf(p);
                  return (
                    <button
                      key={url || i} type="button"
                      onClick={() => { fetchEvents(url); drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                      style={{ ...card, padding: 14, textAlign: 'left', cursor: 'pointer', boxShadow: 'none', background: tk.hover }}
                    >
                      <div className="flex items-center justify-between gap-2" style={{ marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace', color: tk.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{url || '—'}</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: tk.accent, flexShrink: 0 }}>{clicks.toLocaleString('en-IN')}</span>
                      </div>
                      <div style={{ height: 6, borderRadius: 99, background: tk.track, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, borderRadius: 99, background: `linear-gradient(90deg,${tk.accent},${isDark?'#A5A0FF':'#8B5CF6'})` }} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* ── Event drill-down ── */}
        <div ref={drillRef} style={{ ...card, marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${tk.border}` }}>
            <h2 style={{ fontSize: 15, fontWeight: 700, color: tk.text, margin: 0 }}>Event drill-down</h2>
            <p style={{ fontSize: 12, color: tk.sub, margin: '2px 0 0' }}>
              Pull every recorded event for a specific page URL
              {activeFilterCount > 0 && (
                <span style={{ marginLeft: 8, color: tk.accent, fontWeight: 600 }}>
                  · {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''} active
                </span>
              )}
            </p>
          </div>

          <div style={{ padding: 20 }}>
            <div className="flex gap-2.5 mb-5 flex-wrap">
              <input
                value={pageUrl}
                onChange={e => setPageUrl(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchEvents(); }}
                placeholder="https://yoursite.com/page"
                style={{ ...inp, flex: 1, minWidth: 200 }}
                onFocus={focusOn} onBlur={focusOff}
              />
              <button
                type="button" onClick={() => fetchEvents()}
                disabled={eventsLoading || !pageUrl.trim()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
                  background: tk.accent, color: '#fff', border: 'none', borderRadius: 10,
                  padding: '9px 16px', fontSize: 13, fontWeight: 700,
                  cursor: eventsLoading || !pageUrl.trim() ? 'not-allowed' : 'pointer',
                  opacity: eventsLoading || !pageUrl.trim() ? 0.55 : 1,
                }}
              >
                {eventsLoading ? <RefreshCw size={15} className="animate-spin" /> : <Activity size={15} />}
                Fetch events
              </button>
            </div>

            {eventsLoading && (
              <div style={{ display: 'grid', gap: 8 }}>
                {Array(4).fill(null).map((_, i) => <Skel key={i} h={40} r={10} />)}
              </div>
            )}

            {!eventsLoading && events !== null && (
              filteredEvents.length === 0 ? (
                <div style={{ padding: '36px 20px', textAlign: 'center', borderRadius: 12, border: `1px dashed ${tk.border}` }}>
                  <p style={{ fontSize: 13, color: tk.sub, margin: 0 }}>
                    {events.length === 0
                      ? 'No events recorded for this URL yet.'
                      : 'No events match the active filters — try adjusting the date range or type above.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap" style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 12, color: tk.sub, margin: 0 }}>
                      <strong style={{ color: tk.text }}>{filteredEvents.length.toLocaleString('en-IN')}</strong> of {events.length.toLocaleString('en-IN')} events
                      {activeFilterCount > 0 && <span style={{ color: tk.accent }}> (filtered)</span>}
                    </p>
                    <button
                      type="button" onClick={exportCSV}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: `1px solid ${tk.border}`, borderRadius: 9, padding: '7px 12px', color: tk.sub, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      <Download size={13} /> Export CSV
                    </button>
                  </div>

                  {/* desktop table */}
                  <div className="hidden md:block" style={{ overflowX: 'auto', border: `1px solid ${tk.border}`, borderRadius: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 720 }}>
                      <thead>
                        <tr style={{ background: tk.hover }}>
                          {['Event','X','Y','Type','Profile','Tenant','Recorded (IST)'].map(c => (
                            <th key={c} style={{ padding: '10px 14px', textAlign: 'left', color: tk.muted, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', borderBottom: `1px solid ${tk.border}` }}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEvents.map((e, i) => {
                          const pal = typePalette(evType(e), isDark);
                          return (
                            <tr key={evId(e) || i} style={{ borderBottom: `1px solid ${tk.border}` }}>
                              <td style={{ padding: '10px 14px', color: tk.muted, fontFamily: 'ui-monospace,monospace' }}>{evId(e) ? String(evId(e)).slice(0,8) : '—'}</td>
                              <td style={{ padding: '10px 14px', color: tk.sub }}>{evX(e) ?? '—'}</td>
                              <td style={{ padding: '10px 14px', color: tk.sub }}>{evY(e) ?? '—'}</td>
                              <td style={{ padding: '10px 14px' }}>
                                <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: pal.bg, color: pal.solid }}>{pal.label}</span>
                              </td>
                              <td style={{ padding: '10px 14px', color: tk.muted, fontFamily: 'ui-monospace,monospace' }}>{evProfile(e) ? `${String(evProfile(e)).slice(0,8)}…` : '—'}</td>
                              <td style={{ padding: '10px 14px', color: tk.sub }}>{evTenant(e) || '—'}</td>
                              <td style={{ padding: '10px 14px', color: tk.sub, whiteSpace: 'nowrap' }}>{fmtTime(evTime(e))}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* mobile cards */}
                  <div className="md:hidden" style={{ display: 'grid', gap: 9 }}>
                    {filteredEvents.map((e, i) => {
                      const pal = typePalette(evType(e), isDark);
                      return (
                        <div key={evId(e) || i} style={{ border: `1px solid ${tk.border}`, borderRadius: 12, padding: 13 }}>
                          <div className="flex items-center justify-between gap-2" style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: pal.bg, color: pal.solid }}>{pal.label}</span>
                            <span style={{ fontSize: 11, color: tk.muted }}>{fmtTime(evTime(e))}</span>
                          </div>
                          <div className="flex items-center gap-4" style={{ fontSize: 12 }}>
                            <span style={{ color: tk.sub }}>X <strong style={{ color: tk.text }}>{evX(e) ?? '—'}</strong></span>
                            <span style={{ color: tk.sub }}>Y <strong style={{ color: tk.text }}>{evY(e) ?? '—'}</strong></span>
                            <span style={{ color: tk.sub }}>{evTenant(e) || '—'}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )
            )}
          </div>
        </div>

        {/* ── Click distribution map ── */}
        {events !== null && (
          <div style={{ ...card, marginBottom: 20, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${tk.border}` }} className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: tk.text, margin: 0 }}>Click distribution map</h2>
                <p style={{ fontSize: 12, color: tk.sub, margin: '2px 0 0' }}>
                  Coordinates scaled to each event's own viewport
                  {activeFilterCount > 0 && <span style={{ color: tk.accent }}> · filtered view</span>}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 4, padding: 3, background: tk.hover, borderRadius: 10 }}>
                {[{ k: 'points', icon: MousePointerClick, label: 'Points' }, { k: 'heat', icon: Flame, label: 'Heat' }].map(({ k, icon: Ic, label }) => (
                  <button
                    key={k} type="button" onClick={() => setVizMode(k)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
                      borderRadius: 8, padding: '6px 12px', fontSize: 12.5, fontWeight: 600,
                      background: vizMode === k ? tk.bg : 'transparent',
                      color: vizMode === k ? tk.accent : tk.sub,
                      boxShadow: vizMode === k ? tk.shadow : 'none',
                    }}
                  >
                    <Ic size={14} /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ padding: 20 }}>
              {plotted.length === 0 ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, borderRadius: 12, border: `1px dashed ${tk.border}`, color: tk.muted, fontSize: 13 }}>
                  No coordinate data to plot
                </div>
              ) : (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <svg
                      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
                      style={{ width: '100%', minWidth: 320, borderRadius: 12, background: tk.plot, display: 'block', border: `1px solid ${tk.border}` }}
                    >
                      <defs>
                        <filter id="mtBlur" x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur stdDeviation="16" />
                        </filter>
                      </defs>
                      {Array.from({ length: 11 }).map((_, i) => (
                        <line key={`v${i}`} x1={i*(SVG_W/12)} y1={0} x2={i*(SVG_W/12)} y2={SVG_H} stroke={tk.grid} strokeWidth="1" />
                      ))}
                      {Array.from({ length: 6 }).map((_, i) => (
                        <line key={`h${i}`} x1={0} y1={i*(SVG_H/7)} x2={SVG_W} y2={i*(SVG_H/7)} stroke={tk.grid} strokeWidth="1" />
                      ))}
                      {vizMode === 'heat' && (
                        <g filter="url(#mtBlur)">
                          {plotted.map((e, i) => {
                            const cx = clamp((evX(e)/evVW(e))*SVG_W, 0, SVG_W);
                            const cy = clamp((evY(e)/evVH(e))*SVG_H, 0, SVG_H);
                            return <circle key={evId(e)||i} cx={cx} cy={cy} r={26} fill={isDark?'#FBBF24':'#F97316'} opacity={0.32} />;
                          })}
                        </g>
                      )}
                      {vizMode === 'points' && plotted.map((e, i) => {
                        const cx = clamp((evX(e)/evVW(e))*SVG_W, 0, SVG_W);
                        const cy = clamp((evY(e)/evVH(e))*SVG_H, 0, SVG_H);
                        const pal = typePalette(evType(e), isDark);
                        const isHov = hovered && hovered.i === i;
                        return (
                          <circle
                            key={evId(e)||i} cx={cx} cy={cy} r={isHov?10:7}
                            fill={pal.solid} opacity={isHov?0.95:0.72}
                            stroke={isHov?pal.solid:'none'} strokeWidth={isHov?2:0}
                            style={{ cursor: 'pointer', transition: 'r .12s' }}
                            onMouseEnter={() => setHovered({ i, cx, cy, e })}
                            onMouseLeave={() => setHovered(null)}
                          />
                        );
                      })}
                      {vizMode === 'points' && hovered && (
                        <g style={{ pointerEvents: 'none' }}>
                          <rect
                            x={clamp(hovered.cx+12, 4, SVG_W-168)} y={clamp(hovered.cy-40, 4, SVG_H-44)}
                            width={158} height={40} rx={8}
                            fill={isDark?'#1E1E2E':'#0F1623'} opacity={0.96}
                          />
                          <text x={clamp(hovered.cx+12,4,SVG_W-168)+12} y={clamp(hovered.cy-40,4,SVG_H-44)+17} fill="#fff" fontSize="11.5" fontWeight="700" fontFamily="ui-monospace,monospace">
                            {typePalette(evType(hovered.e),isDark).label} · {evX(hovered.e)},{evY(hovered.e)}
                          </text>
                          <text x={clamp(hovered.cx+12,4,SVG_W-168)+12} y={clamp(hovered.cy-40,4,SVG_H-44)+31} fill="rgba(255,255,255,0.7)" fontSize="10" fontFamily="ui-monospace,monospace">
                            {fmtTime(evTime(hovered.e))}
                          </text>
                        </g>
                      )}
                    </svg>
                  </div>
                  <div className="flex items-center gap-4 flex-wrap" style={{ marginTop: 14 }}>
                    {vizMode === 'points'
                      ? ['click','scroll','hover'].map(t => {
                          const pal = typePalette(t, isDark);
                          return (
                            <div key={t} className="flex items-center gap-2">
                              <span style={{ width: 10, height: 10, borderRadius: 99, background: pal.solid }} />
                              <span style={{ fontSize: 12, color: tk.sub }}>{pal.label}</span>
                            </div>
                          );
                        })
                      : (
                        <div className="flex items-center gap-2">
                          <span style={{ width: 60, height: 10, borderRadius: 99, background: `linear-gradient(90deg,transparent,${isDark?'#FBBF24':'#F97316'})` }} />
                          <span style={{ fontSize: 12, color: tk.sub }}>Higher density = warmer</span>
                        </div>
                      )
                    }
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Record test event ── */}
        <div style={{ ...card, marginBottom: 28, overflow: 'hidden' }}>
          <button
            type="button" onClick={() => setRecordOpen(o => !o)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '16px 20px', background: 'transparent', border: 'none', cursor: 'pointer',
              borderBottom: recordOpen ? `1px solid ${tk.border}` : 'none',
            }}
          >
            <div className="flex items-center gap-3">
              <div style={{ width: 30, height: 30, borderRadius: 9, background: typePalette('hover',isDark).bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Plus size={16} color={typePalette('hover',isDark).solid} />
              </div>
              <span style={{ fontSize: 15, fontWeight: 700, color: tk.text }}>Record a test event</span>
            </div>
            {recordOpen ? <ChevronUp size={16} color={tk.sub} /> : <ChevronDown size={16} color={tk.sub} />}
          </button>

          {recordOpen && (
            <div className="mt-fade" style={{ padding: 20 }}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {[
                  { key: 'page_url',  label: 'Page URL',     placeholder: 'https://yoursite.com/page', type: 'text'   },
                  { key: 'tenant_id', label: 'Tenant ID',    placeholder: 'hospital_001',              type: 'text'   },
                  { key: 'x_coord',   label: 'X coordinate', placeholder: '320',                       type: 'number' },
                  { key: 'y_coord',   label: 'Y coordinate', placeholder: '480',                       type: 'number' },
                ].map(({ key, label, placeholder, type }) => (
                  <div key={key}>
                    <label style={{ fontSize: 10.5, fontWeight: 700, color: tk.muted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>{label}</label>
                    <input
                      type={type} value={form[key]}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      placeholder={placeholder}
                      style={{ ...inp, width: '100%' }}
                      onFocus={focusOn} onBlur={focusOff}
                    />
                  </div>
                ))}
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 10.5, fontWeight: 700, color: tk.muted, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>Event type</label>
                <select
                  value={form.event_type}
                  onChange={e => setForm(f => ({ ...f, event_type: e.target.value }))}
                  style={{ ...inp, width: 200, appearance: 'none' }}
                >
                  <option value="click">Click</option>
                  <option value="scroll">Scroll</option>
                  <option value="hover">Hover</option>
                </select>
              </div>
              <button
                type="button" onClick={recordEvent}
                disabled={recording || !form.page_url.trim()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: typePalette('hover',isDark).solid, color: '#fff', border: 'none',
                  borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 700,
                  cursor: recording || !form.page_url.trim() ? 'not-allowed' : 'pointer',
                  opacity: recording || !form.page_url.trim() ? 0.55 : 1,
                }}
              >
                {recording ? <RefreshCw size={15} className="animate-spin" /> : <Plus size={15} />}
                {recording ? 'Recording…' : 'Record event'}
              </button>
            </div>
          )}
        </div>

      </main>
    </div>
  );
}